import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import '../styles/ItemForm.css';
import { createUbicacion, updateUbicacion } from '../services/ubicaciones';
import { Ubicacion } from '../types';

interface UbicacionFormProps {
    onClose: () => void;
    onSuccess: (message: string) => void;
    itemData?: Ubicacion;
}

const UbicacionForm: React.FC<UbicacionFormProps> = ({ onClose, onSuccess, itemData }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        zona_abc: 'C',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (itemData) {
            setFormData({
                nombre: itemData.nombre || '',
                descripcion: itemData.descripcion || '',
                zona_abc: itemData.zona_abc || 'C',
            });
        }
    }, [itemData]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError(null);
    };

    const validateName = (name: string) => {
        const regex = /^[A-Za-z0-9]+-\d{2}-\d{2}$/;
        return regex.test(name);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateName(formData.nombre)) {
            setError('El formato del nombre debe ser PASILLO-MODULO-NIVEL (ej. A-03-02)');
            return;
        }

        setLoading(true);
        try {
            if (itemData?.id) {
                await updateUbicacion(itemData.id, formData as any);
                onSuccess(`¡Ubicación actualizada correctamente!`);
            } else {
                await createUbicacion(formData as any);
                onSuccess(`¡Ubicación creada correctamente!`);
            }
            setFormData({ nombre: '', descripcion: '', zona_abc: 'C' });
        } catch (error: any) {
            console.error(`Error al ${itemData?.id ? 'actualizar' : 'crear'} ubicación:`, error);
            setError(error.response?.data?.message || 'Error occurred while saving location');
        } finally {
            setLoading(false);
            if (!error && (itemData?.id || !error)) {
                // do not close on error, only on success if the save was fired.
                // we added the try catch to catch server errors, so if no error thrown by API, we close.
            }
            onClose(); // In a real scenario you might want to only close on absolute success.
        }
    };

    return (
        <form className="item-form" onSubmit={handleSubmit}>
            <h3 className="form-title">{itemData?.id ? `Editar Ubicación` : `Crear Ubicación`}</h3>
            {error && <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

            <label>
                Nombre (PASILLO-MODULO-NIVEL):
                <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ejemplo: A-03-02"
                    required
                />
            </label>
            <label>
                Zona ABC (Prioridad Picking):
                <select
                    name="zona_abc"
                    value={formData.zona_abc}
                    onChange={handleChange}
                    required
                >
                    <option value="A">Zona A</option>
                    <option value="B">Zona B</option>
                    <option value="C">Zona C</option>
                </select>
            </label>

            <label>
                Descripción:
                <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Descripción (opcional)"
                />
            </label>
            <div className="button-group">
                <button type="submit" className="button primary" disabled={loading}>
                    {loading ? 'Guardando...' : itemData?.id ? 'Actualizar' : 'Crear'}
                </button>
                <button
                    type="button"
                    className="button secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancelar
                </button>
            </div>
        </form>
    );
};

export default UbicacionForm;
